/**
 * UpgradeRequiredView (Iter 73i) — Wiederverwendbare In-Page-Card für Free-User,
 * die ein Pro-Feature öffnen. Konsistentes Layout über alle gating-Stellen
 * (`/m/scan`, `/steuerberater-bereich`, etc.).
 *
 * Verwendung:
 *   <UpgradeRequiredView
 *     title="Steuerberater-Anbindung"
 *     subtitle="Direkter Draht zu deinem Steuerberater"
 *     features={["…", "…"]}
 *   />
 */
import React, { useEffect } from 'react';
import { Lock, Sparkles, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { useUpgradeTracking } from '@/hooks/useUpgradeTracking';

export default function UpgradeRequiredView({
  title = 'Pro-Feature',
  subtitle = 'Diese Funktion ist Teil von ivoliq Pro.',
  features = [],
  icon: Icon = Lock,
  className = '',
  source = 'unknown',  // Iter 73j: für Conversion-Tracking
}) {
  const track = useUpgradeTracking();

  // View-Tracking beim Mount
  useEffect(() => {
    track('view', source);
  }, [track, source]);

  const onCtaClick = () => track('click', source);

  return (
    <div className={`max-w-2xl mx-auto px-4 py-10 ${className}`} data-testid="upgrade-required-view" data-source={source}>
      <Card className="border-emerald-200/60 shadow-md overflow-hidden">
        <div className="bg-gradient-to-br from-emerald-50 via-white to-emerald-50 px-6 pt-8 pb-6 border-b border-emerald-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Icon className="h-5 w-5" />
            </div>
            <span className="text-xs font-semibold tracking-wider text-emerald-700 bg-emerald-100/70 rounded-full px-2.5 py-0.5">
              ivoliq Pro
            </span>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-1.5" data-testid="upgrade-required-title">
            {title}
          </h2>
          <p className="text-sm text-slate-600">{subtitle}</p>
        </div>

        <CardContent className="px-6 py-6 space-y-5">
          {features.length > 0 && (
            <ul className="space-y-2.5">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Link to="/pricing" className="flex-1" onClick={onCtaClick}>
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="upgrade-cta-primary"
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                Auf Pro upgraden
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
            <Link to="/pricing" className="sm:flex-shrink-0" onClick={onCtaClick}>
              <Button variant="outline" className="w-full sm:w-auto" data-testid="upgrade-cta-info">
                Pläne vergleichen
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
